import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  History, 

  LogOut,
  Settings,
  Sidebar as SidebarIcon,
  MessageSquare,
  X
} from 'lucide-react';

const ChatSidebar = ({ 
  sessions = [], 
  activeSessionId, 
  onSelectSession, 
  onNewChat, 
  onDeleteSession, 
  isCollapsed,
  onToggleCollapse,
  currentPage,
  totalSessions,
  onPageChange,
  pageSize = 10,
  isLoading,
  user,
  onLogout,
  onAdminPanel,
  logoUrl
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeNav, setActiveNav] = useState('history');
  const [isLogoHovered, setIsLogoHovered] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [modalSearchQuery, setModalSearchQuery] = useState('');

  // Always reset hover state when sidebar collapses/expands so logo is displayed normally
  useEffect(() => {
    setIsLogoHovered(false);
  }, [isCollapsed]);

  // Close modal on ESC key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && showHistoryModal) {
        setShowHistoryModal(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showHistoryModal]);


  // Filter sessions by search
  const filteredSessions = sessions.filter(s => {
    if (!searchQuery.trim()) return true;
    const summary = String(s?.summary || '').toLowerCase();
    return summary.includes(searchQuery.toLowerCase());
  });

  // Categorize sessions into Today, Yesterday, 7 days, Older
  const categorizeSessions = (list) => {
    const today = [];
    const yesterday = [];
    const pastWeek = [];
    const older = [];

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startOfYesterday = startOfToday - 86400000;
    const startOfWeek = startOfToday - 7 * 86400000;

    list.forEach(s => {
      const raw = s.updated_at || s.created_at;
      const ms = raw ? (raw < 10000000000 ? raw * 1000 : raw) : Date.now();
      if (ms >= startOfToday) {
        today.push(s);
      } else if (ms >= startOfYesterday) {
        yesterday.push(s);
      } else if (ms >= startOfWeek) {
        pastWeek.push(s);
      } else {
        older.push(s);
      }
    });

    return { today, yesterday, pastWeek, older };
  };

  const groups = categorizeSessions(filteredSessions);

  const currentUser = user || (() => {
    try {
      const str = sessionStorage.getItem('user') || localStorage.getItem('user');
      return str ? JSON.parse(str) : null;
    } catch (e) {
      return null;
    }
  })();

  const username = currentUser?.username || 'User';

  return (
    <aside
      className="copilot-sidebar-mobile"
      data-collapsed={isCollapsed ? "true" : "false"}
      style={{
        width: isCollapsed ? '56px' : '260px',
        minWidth: isCollapsed ? '56px' : '260px',
        height: '100%',
        backgroundColor: '#fafafb',
        borderRight: '1px solid #f1f2f4',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.25s cubic-bezier(0.16, 1, 0.3, 1), min-width 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
        overflow: 'hidden',
        position: 'relative',
        zIndex: 20
      }}
    >
      {isCollapsed ? (
        /* Collapsed Mini Rail matching ChatGPT reference */
        <div style={{
          width: '56px',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 0 16px 0',
          boxSizing: 'border-box'
        }}>
          {/* Top Icons */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', width: '100%' }}>
            {/* Logo: Clicking brings back the sidebar. Swaps to SidebarIcon on hover */}
            <button
              onClick={onToggleCollapse}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.06)';
                e.currentTarget.style.backgroundColor = '#f4f4f5';
                setIsLogoHovered(true);
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.backgroundColor = 'transparent';
                setIsLogoHovered(false);
              }}
              style={{
                width: '38px',
                height: '38px',
                borderRadius: '10px',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                color: '#52525b',
                transition: 'transform 0.15s ease, background 0.15s ease'
              }}
              title="Open sidebar"
            >
              {isLogoHovered ? (
                <SidebarIcon size={19} strokeWidth={2.2} />
              ) : (
                <img
                  src={logoUrl || "/default.png"}
                  alt="EasyChat"
                  onError={(e) => {
                    if (!e.currentTarget.src.endsWith('/default.png')) {
                      e.currentTarget.src = '/default.png';
                    }
                  }}
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '6px',
                    objectFit: 'contain'
                  }}
                />
              )}
            </button>

            {/* New Chat Button */}
            <button
              onClick={onNewChat}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '9px',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#27272a',
                transition: 'background 0.15s ease'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f4f4f5')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              title="New chat"
            >
              <Plus size={18} strokeWidth={2.2} />
            </button>

            {/* Search Button: Opens History & Search modal */}
            <button
              onClick={() => setShowHistoryModal(true)}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '9px',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#71717a',
                transition: 'background 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f4f4f5';
                e.currentTarget.style.color = '#18181b';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#71717a';
              }}
              title="Search conversations"
            >
              <Search size={18} />
            </button>
          </div>

          {/* Bottom Avatar icon in mini rail */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '34px',
                height: '34px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #f472b6 0%, #ec4899 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
                fontWeight: 700,
                fontSize: '12px',
                boxShadow: '0 2px 6px rgba(236, 72, 153, 0.25)',
                cursor: 'pointer'
              }}
              onClick={onToggleCollapse}
              title={`${username} (Open sidebar)`}
            >
              {username.slice(0, 2).toUpperCase()}
            </div>
          </div>
        </div>
      ) : (
        /* Full Expanded Sidebar */
        <>
          {/* Brand Header */}
          <div style={{ padding: '20px 18px 14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <img
                src={logoUrl || "/default.png"}
                alt="EasyChat"
                onError={(e) => {
                  if (!e.currentTarget.src.endsWith('/default.png')) {
                    e.currentTarget.src = '/default.png';
                  }
                }}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  objectFit: 'contain'
                }}
              />
              <span style={{
                fontWeight: 800,
                fontSize: '1.15rem',
                color: '#18181b',
                letterSpacing: '-0.02em',
                fontFamily: "'Inter', sans-serif"
              }}>
                EasyChat
              </span>
            </div>

            {onToggleCollapse && (
              <button
                onClick={() => {
                  setIsLogoHovered(false);
                  onToggleCollapse();
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#71717a',
                  padding: '6px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.15s'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f4f4f5')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                title="Collapse sidebar"
              >
                <SidebarIcon size={18} />
              </button>
            )}
          </div>

      {/* New Chat Button */}
      <div style={{ padding: '0 16px 12px 16px' }}>
        <button
          onClick={onNewChat}
          style={{
            width: '100%',
            height: '42px',
            background: '#18181b',
            color: '#ffffff',
            border: 'none',
            borderRadius: '21px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            fontSize: '0.88rem',
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
            transition: 'all 0.15s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#27272a';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#18181b';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <Plus size={16} strokeWidth={2.5} />
          <span>New chat</span>
        </button>
      </div>

      {/* Search Input */}
      <div style={{ padding: '0 16px 14px 16px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          background: '#ffffff',
          border: '1px solid #e4e4e7',
          borderRadius: '12px',
          padding: '0 10px',
          height: '36px',
          gap: '8px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
        }}>
          <Search size={14} style={{ color: '#a1a1aa', flexShrink: 0 }} />
          <input
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              border: 'none',
              background: 'transparent',
              outline: 'none',
              fontSize: '0.82rem',
              color: '#18181b',
              width: '100%'
            }}
          />
          <kbd style={{
            fontSize: '10px',
            padding: '2px 5px',
            background: '#f4f4f5',
            color: '#71717a',
            borderRadius: '5px',
            border: '1px solid #e4e4e7',
            fontFamily: 'inherit'
          }}>
            ⌘K
          </kbd>
        </div>
      </div>

      {/* Primary Navigation Menu */}
      <div style={{ padding: '0 12px 12px 12px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <button
          onClick={() => {
            setActiveNav('history');
            setShowHistoryModal(true);
          }}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '8px 12px',
            borderRadius: '10px',
            background: activeNav === 'history' ? '#f4f4f5' : 'transparent',
            color: activeNav === 'history' ? '#18181b' : '#71717a',
            border: 'none',
            cursor: 'pointer',
            fontSize: '0.86rem',
            fontWeight: activeNav === 'history' ? 600 : 500,
            textAlign: 'left',
            transition: 'all 0.15s ease'
          }}
          onMouseEnter={(e) => {
            if (activeNav !== 'history') e.currentTarget.style.backgroundColor = '#f4f4f5';
          }}
          onMouseLeave={(e) => {
            if (activeNav !== 'history') e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <History size={16} strokeWidth={activeNav === 'history' ? 2.2 : 1.8} style={{ color: activeNav === 'history' ? '#c084fc' : '#a1a1aa' }} />
          <span>History</span>
        </button>
      </div>

      {/* Divider */}
      <div style={{ height: '1px', background: '#f1f2f4', margin: '0 16px 8px 16px' }} />

      {/* Categorized Session History */}
      <div
        className="custom-scrollbar"
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '6px 12px 12px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px'
        }}
      >
        {isLoading ? (
          <div style={{ padding: '24px', display: 'flex', justifyContent: 'center' }}>
            <div style={{
              width: '18px',
              height: '18px',
              border: '2px solid #e4e4e7',
              borderTop: '2px solid #c084fc',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
          </div>
        ) : filteredSessions.length === 0 ? (
          <div style={{ padding: '20px 12px', textAlign: 'center', color: '#a1a1aa', fontSize: '0.78rem' }}>
            No recent conversations
          </div>
        ) : (
          <>
            {[
              { label: 'Today', items: groups.today },
              { label: 'Yesterday', items: groups.yesterday },
              { label: '7 days', items: groups.pastWeek },
              { label: 'Older', items: groups.older }
            ].map(
              (section) =>
                section.items.length > 0 && (
                  <div key={section.label} style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    <span style={{
                      fontSize: '11px',
                      color: '#a1a1aa',
                      fontWeight: 600,
                      padding: '4px 10px',
                      letterSpacing: '-0.01em'
                    }}>
                      {section.label}
                    </span>
                    {section.items.map((session) => {
                      const isActive = activeSessionId === session.session_id;
                      const title = typeof session.summary === 'string'
                        ? session.summary.replace(/\s*<additional context>[\s\S]*?(?:<\/additional context>|$)/g, '').trim()
                        : 'Conversation';

                      return (
                        <div
                          key={session.session_id}
                          onClick={() => onSelectSession(session)}
                          style={{
                            padding: '8px 12px',
                            borderRadius: '9px',
                            background: isActive ? '#ffffff' : 'transparent',
                            color: isActive ? '#18181b' : '#52525b',
                            fontSize: '0.83rem',
                            fontWeight: isActive ? 600 : 450,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '8px',
                            boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.04), 0 0 0 1px #e4e4e7' : 'none',
                            transition: 'all 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (!isActive) e.currentTarget.style.backgroundColor = '#f4f4f5';
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <span style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            flex: 1
                          }}>
                            {title || 'Untitled session'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )
            )}
          </>
        )}
      </div>

      {/* Bottom User Card / Profile */}
      <div style={{
        padding: '12px 14px',
        borderTop: '1px solid #f1f2f4',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: '#ffffff'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
          <div style={{
            width: '34px',
            height: '34px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #fed7aa 0%, #fbcfe8 50%, #c4b5fd 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '13px',
            fontWeight: 700,
            color: '#4c1d95',
            flexShrink: 0,
            border: '1px solid #e4e4e7'
          }}>
            {username.charAt(0).toUpperCase()}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <span style={{
              fontSize: '0.82rem',
              fontWeight: 600,
              color: '#18181b',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {username}
            </span>
            <span style={{
              fontSize: '0.72rem',
              color: '#a1a1aa',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {onAdminPanel && (currentUser?.role === 'admin' || currentUser?.role === 'super_admin') && (
            <button
              onClick={onAdminPanel}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: '#71717a',
                padding: '6px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f4f4f5';
                e.currentTarget.style.color = '#4f46e5';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#71717a';
              }}
              title="Admin Panel"
            >
              <Settings size={16} />
            </button>
          )}
          {onLogout && (
            <button
              onClick={onLogout}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: '#71717a',
              padding: '6px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f4f4f5';
              e.currentTarget.style.color = '#ef4444';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#71717a';
            }}
            title="Log out"
            >
              <LogOut size={16} />
            </button>
          )}
        </div>
      </div>
      </>
      )}

      {/* History and Search Modal with Copilot Theme */}
      {showHistoryModal && (
        <div
          onClick={() => setShowHistoryModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(15, 23, 42, 0.28)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            padding: '16px',
            boxSizing: 'border-box',
            animation: 'fadeInModal 0.15s ease-out'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: '540px',
              maxHeight: '80vh',
              background: '#ffffff',
              borderRadius: '20px',
              border: '1px solid #e4e4e7',
              boxShadow: '0 20px 45px -10px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(192, 132, 252, 0.08)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              animation: 'scaleInModal 0.2s cubic-bezier(0.16, 1, 0.3, 1)'
            }}
          >
            {/* Header: Search Input & Close Button */}
            <div style={{
              padding: '14px 18px',
              borderBottom: '1px solid #f1f2f4',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <Search size={18} style={{ color: '#a1a1aa', flexShrink: 0 }} />
              <input
                type="text"
                autoFocus
                value={modalSearchQuery}
                onChange={(e) => setModalSearchQuery(e.target.value)}
                placeholder="Search chats..."
                style={{
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  fontSize: '0.96rem',
                  color: '#18181b',
                  background: 'transparent',
                  fontFamily: 'inherit'
                }}
              />
              <button
                onClick={() => setShowHistoryModal(false)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  padding: '6px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#71717a',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f4f4f5';
                  e.currentTarget.style.color = '#18181b';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#71717a';
                }}
                title="Close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Content List: Last opened & Recent chats */}
            <div
              className="custom-scrollbar"
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '16px 14px',
                display: 'flex',
                flexDirection: 'column',
                gap: '18px'
              }}
            >
              {(() => {
                const query = modalSearchQuery.trim().toLowerCase();
                const matched = sessions.filter((s) => {
                  if (!query) return true;
                  const text = String(s?.summary || '').toLowerCase();
                  return text.includes(query);
                });

                if (matched.length === 0) {
                  return (
                    <div style={{
                      padding: '40px 20px',
                      textAlign: 'center',
                      color: '#a1a1aa',
                      fontSize: '0.9rem'
                    }}>
                      {modalSearchQuery ? 'No chats found matching your search' : 'No chat history available'}
                    </div>
                  );
                }

                // If searching, show as a single direct search results list
                if (query) {
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <span style={{
                        fontSize: '0.78rem',
                        fontWeight: 600,
                        color: '#a1a1aa',
                        padding: '0 10px 4px 10px',
                        letterSpacing: '-0.01em'
                      }}>
                        Search Results ({matched.length})
                      </span>
                      {matched.map((session) => {
                        const title = typeof session.summary === 'string'
                          ? session.summary.replace(/\s*<additional context>[\s\S]*?(?:<\/additional context>|$)/g, '').trim()
                          : 'Conversation';
                        const isCurrent = activeSessionId === session.session_id;

                        return (
                          <div
                            key={session.session_id}
                            onClick={() => {
                              onSelectSession(session);
                              setShowHistoryModal(false);
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              padding: '10px 12px',
                              borderRadius: '12px',
                              cursor: 'pointer',
                              transition: 'all 0.15s ease',
                              background: isCurrent ? '#faf5ff' : 'transparent',
                              border: isCurrent ? '1px solid #e9d5ff' : '1px solid transparent'
                            }}
                            onMouseEnter={(e) => {
                              if (!isCurrent) e.currentTarget.style.backgroundColor = '#f4f4f5';
                            }}
                            onMouseLeave={(e) => {
                              if (!isCurrent) e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            <MessageSquare
                              size={16}
                              style={{
                                color: isCurrent ? '#a855f7' : '#9ca3af',
                                flexShrink: 0
                              }}
                            />
                            <span style={{
                              fontSize: '0.88rem',
                              color: isCurrent ? '#7e22ce' : '#27272a',
                              fontWeight: isCurrent ? 600 : 450,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {title || 'Untitled chat'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  );
                }

                // Normal view matching reference: "Last opened" (top 3) and "Recent chats" (the rest)
                const lastOpened = matched.slice(0, 3);
                const recentChats = matched.slice(3);

                return (
                  <>
                    {/* Last opened */}
                    {lastOpened.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{
                          fontSize: '0.78rem',
                          fontWeight: 600,
                          color: '#a1a1aa',
                          padding: '0 10px 4px 10px',
                          letterSpacing: '-0.01em'
                        }}>
                          Last opened
                        </span>
                        {lastOpened.map((session) => {
                          const title = typeof session.summary === 'string'
                            ? session.summary.replace(/\s*<additional context>[\s\S]*?(?:<\/additional context>|$)/g, '').trim()
                            : 'Conversation';
                          const isCurrent = activeSessionId === session.session_id;

                          return (
                            <div
                              key={session.session_id}
                              onClick={() => {
                                onSelectSession(session);
                                setShowHistoryModal(false);
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '10px 12px',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                                background: isCurrent ? '#faf5ff' : 'transparent',
                                border: isCurrent ? '1px solid #e9d5ff' : '1px solid transparent'
                              }}
                              onMouseEnter={(e) => {
                                if (!isCurrent) e.currentTarget.style.backgroundColor = '#f4f4f5';
                              }}
                              onMouseLeave={(e) => {
                                if (!isCurrent) e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <MessageSquare
                                size={16}
                                style={{
                                  color: isCurrent ? '#a855f7' : '#9ca3af',
                                  flexShrink: 0
                                }}
                              />
                              <span style={{
                                fontSize: '0.88rem',
                                color: isCurrent ? '#7e22ce' : '#27272a',
                                fontWeight: isCurrent ? 600 : 450,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}>
                                {title || 'Untitled chat'}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Recent chats */}
                    {recentChats.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{
                          fontSize: '0.78rem',
                          fontWeight: 600,
                          color: '#a1a1aa',
                          padding: '0 10px 4px 10px',
                          letterSpacing: '-0.01em'
                        }}>
                          Recent chats
                        </span>
                        {recentChats.map((session) => {
                          const title = typeof session.summary === 'string'
                            ? session.summary.replace(/\s*<additional context>[\s\S]*?(?:<\/additional context>|$)/g, '').trim()
                            : 'Conversation';
                          const isCurrent = activeSessionId === session.session_id;

                          return (
                            <div
                              key={session.session_id}
                              onClick={() => {
                                onSelectSession(session);
                                setShowHistoryModal(false);
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '10px 12px',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                                background: isCurrent ? '#faf5ff' : 'transparent',
                                border: isCurrent ? '1px solid #e9d5ff' : '1px solid transparent'
                              }}
                              onMouseEnter={(e) => {
                                if (!isCurrent) e.currentTarget.style.backgroundColor = '#f4f4f5';
                              }}
                              onMouseLeave={(e) => {
                                if (!isCurrent) e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <MessageSquare
                                size={16}
                                style={{
                                  color: isCurrent ? '#a855f7' : '#9ca3af',
                                  flexShrink: 0
                                }}
                              />
                              <span style={{
                                fontSize: '0.88rem',
                                color: isCurrent ? '#7e22ce' : '#27272a',
                                fontWeight: isCurrent ? 600 : 450,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}>
                                {title || 'Untitled chat'}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeInModal {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleInModal {
          from { opacity: 0; transform: scale(0.96); }
          to { opacity: 1; transform: scale(1); }
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #e4e4e7;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #d4d4d8;
        }
      `}</style>
    </aside>
  );
};

export default ChatSidebar;

